/**
 * Defines the contract of the posted book notice (Anexo III) with its QR code.
 */
import { z } from "zod";
import { PublicSlugSchema } from "./PlatformContract.js";
/**
 * Query of the notice endpoints. The branch travels as its public slug, the
 * same identifier the book URL carries. Without a branch the notice points at
 * the company book, which is what a website snippet normally needs.
 */
export const BookNoticeQuerySchema = z
    .object({
    branchSlug: PublicSlugSchema.optional(),
})
    .strict();
export const BookNoticeCompanySchema = z
    .object({
    legalName: z.string().min(1).max(200),
    taxId: z.string().regex(/^\d{11}$/u),
    tradeName: z.string().max(200).nullable(),
})
    .strict();
export const BookNoticeBranchSchema = z
    .object({
    address: z.string().max(500).nullable(),
    name: z.string().min(1).max(200),
    slug: PublicSlugSchema,
})
    .strict();
/**
 * Everything needed to print the establishment poster or paste the web notice.
 */
export const BookNoticeSchema = z
    .object({
    bookUrl: z.url().max(2_000),
    branch: BookNoticeBranchSchema.nullable(),
    /**
     * Compact, self-contained call-to-action: a pill-shaped link with a book
     * icon, meant to be dropped anywhere on the company site (a footer, a
     * support page) rather than occupy a dedicated panel.
     */
    buttonSnippet: z.string().min(1).max(5_000),
    company: BookNoticeCompanySchema,
    generatedAt: z.iso.datetime({ offset: true }),
    /**
     * Ready-to-paste, self-contained markup for the company website.
     */
    htmlSnippet: z.string().min(1).max(20_000),
    /**
     * Mandatory wording of Anexo III, adapted to the virtual book.
     */
    legalNotice: z.string().min(1).max(1_000),
    /**
     * The company's branding logo, inlined as a data URL for the printed
     * poster only — neither web snippet repeats it, since both are already
     * embedded in the company's own branded page. Absent when the company
     * has no logo configured or it could not be fetched safely.
     */
    logoDataUrl: z.string().max(210_000).nullable(),
    primaryColor: z.string().regex(/^#[0-9A-F]{6}$/u),
    qrSvg: z.string().min(1).max(200_000),
    scanInstruction: z.string().min(1).max(500),
    title: z.string().min(1).max(120),
})
    .strict();
//# sourceMappingURL=BookNoticeContract.js.map